import type { Game } from '../../core/game';
import type { ThrowResult } from '../../core/types';
import { combatLog, lastCombatLog, throwDetail, type CombatLog, type CombatThrow, type PoisonKill, type ThrowDetail } from '../combatApi';
import { debugEnabledByChannel as isDebugEnabled } from '../channel';
import { corruptedFactor, dieLevelOf, levelFactorOf } from '../abyssApi';
import { formatDamage, formatGold, formatLevelFactor, formatNumber, formatPercent1, formatSeconds } from '../format';
import { triggeredSpellText, triggeredSpells } from '../spellsApi';
import { overlayRoot } from '../columnScale';
import { icon } from '../icons';
import { registerEscape } from '../keyboard';

/**
 * « Récapitulatif des dégâts » — COMMENTS v0.3.0 §2.
 *
 * Affiché à la fin de **chaque** Expédition et de **chaque** Invasion
 * (`expeditionEnd`, `invasionWon`, `runLost`). Section repliable :
 *  1. un en-tête de métriques (lancers, moyenne, médiane, 1er quartile, min,
 *     max, total, DPS, critiques, sorts, % de lancers avec sort), le poison et
 *     le tableau des sorts (A122) ;
 *  2. la liste des lancers, **du plus récent au plus ancien**, en lignes
 *     compactes `#n · t=12,3 s · Squelette · 14,5 dégâts · [CRIT] [Étincelle]` ;
 *  3. au tap sur une ligne : **hors debug** un résumé court (A122-10 : somme des
 *     faces Maîtres, contenant, sorts, reports, Électrification, ArchiDicer,
 *     dégâts infligés) ; **en debug** le détail du calcul à la granularité de
 *     COMMENTS §1.2.5 (D_L, D_C, D_S, M_P, M_T, affixes, Total) et la liste des
 *     faces de chaque dé. Les morts par poison s'intercalent à leur instant.
 *
 * La source est `state.combat.throws`, livrée par l'agent `src/core` en
 * parallèle : sans elle, la section affiche « Historique indisponible ».
 */

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text !== undefined) node.textContent = text;
  return node;
}

/** « 12,3 s » sans espace insécable parasite (les lignes sont déjà denses). */
function atLabel(seconds: number): string {
  return `t=${formatNumber(seconds, 1)} s`;
}

/** Ligne « nom : valeur » de l'en-tête de métriques. */
function metric(label: string, value: string, role?: string): HTMLElement {
  const item = el('div', 'recap-metric');
  item.append(el('dt', 'recap-metric-label', label), el('dd', 'recap-metric-value', value));
  if (role) item.setAttribute('data-metric', role);
  return item;
}

/**
 * Quantile par **interpolation linéaire** (méthode « type 7 », celle des
 * tableurs) : `q` dans [0, 1] ; 0 pour une liste vide. Médiane = 0,5, 1er
 * quartile = 0,25.
 */
export function quantile(values: readonly number[], q: number): number {
  if (values.length === 0) return 0;
  const sorted = [...values].sort((a, b) => a - b);
  const pos = Math.min(1, Math.max(0, q)) * (sorted.length - 1);
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

/** Une ligne du tableau des sorts : déclenchements et part des lancers qui l'ont eu. */
export interface SpellRow {
  id: string;
  label: string;
  /** Nombre de déclenchements (copies comprises). */
  count: number;
  /** Lancers où le sort est tombé au moins une fois. */
  throws: number;
  /** `throws` / nombre de lancers (0 à 1). */
  share: number;
}

interface Metrics {
  count: number;
  mean: number;
  median: number;
  q1: number;
  min: number;
  max: number;
  total: number;
  dps: number;
  crits: number;
  /** Sorts déclenchés, tous lancers confondus. */
  spells: number;
  /** Lancers avec au moins un sort, en fraction. */
  spellShare: number;
  spellRows: SpellRow[];
  duration: number;
}

/** Tableau des sorts : déclenchements décroissants (à égalité : nom). */
export function spellRows(throws: readonly CombatThrow[]): SpellRow[] {
  const rows = new Map<string, SpellRow>();
  for (const t of throws) {
    const seen = new Set<string>();
    for (const sp of triggeredSpells(t.result)) {
      const row = rows.get(sp.id) ?? { id: sp.id, label: sp.label, count: 0, throws: 0, share: 0 };
      row.count += 1;
      if (!seen.has(sp.id)) {
        seen.add(sp.id);
        row.throws += 1;
      }
      rows.set(sp.id, row);
    }
  }
  const n = throws.length;
  return [...rows.values()]
    .map((r) => ({ ...r, share: n > 0 ? r.throws / n : 0 }))
    .sort((a, b) => b.count - a.count || a.label.localeCompare(b.label, 'fr'));
}

export function computeMetrics(throws: CombatThrow[], duration: number): Metrics {
  const totals = throws.map((t) => t.result.total);
  const total = totals.reduce((s, n) => s + n, 0);
  const rows = spellRows(throws);
  return {
    count: throws.length,
    mean: throws.length > 0 ? total / throws.length : 0,
    median: quantile(totals, 0.5),
    q1: quantile(totals, 0.25),
    min: throws.length > 0 ? Math.min(...totals) : 0,
    max: throws.length > 0 ? Math.max(...totals) : 0,
    total,
    dps: duration > 0 ? total / duration : 0,
    crits: throws.filter((t) => t.result.crit === true).length,
    spells: rows.reduce((s, r) => s + r.count, 0),
    spellShare: throws.length > 0 ? throws.filter((t) => triggeredSpells(t.result).length > 0).length / throws.length : 0,
    spellRows: rows,
    duration,
  };
}

function buildMetrics(m: Metrics): HTMLElement {
  const list = el('dl', 'recap-metrics');
  list.setAttribute('data-role', 'recap-metrics');
  list.append(
    metric('Lancers', String(m.count), 'count'),
    metric('Moyenne', formatDamage(m.mean), 'mean'),
    metric('Médiane', formatDamage(m.median), 'median'),
    metric('1er quartile', formatDamage(m.q1), 'q1'),
    metric('Min', formatDamage(m.min), 'min'),
    metric('Max', formatDamage(m.max), 'max'),
    metric('Total', formatDamage(m.total), 'total'),
    metric('Dégâts/seconde', formatDamage(m.dps), 'dps'),
    metric('Critiques', String(m.crits), 'crits'),
    metric('Sorts', String(m.spells), 'spells'),
    metric('Lancers avec sort', formatPercent1(m.spellShare), 'spell-share'),
  );
  return list;
}

/** Tableau par sort (`[data-role="recap-spells"]`) : nom, déclenchements, % des lancers. */
function buildSpellTable(rows: readonly SpellRow[]): HTMLElement | null {
  if (rows.length === 0) return null;
  const table = el('table', 'recap-spells');
  table.setAttribute('data-role', 'recap-spells');
  const head = el('thead');
  const hr = el('tr');
  for (const h of ['Sort', 'Déclenchements', '% des lancers']) hr.appendChild(el('th', undefined, h));
  head.appendChild(hr);
  table.appendChild(head);
  const body = el('tbody');
  for (const r of rows) {
    const tr = el('tr');
    tr.setAttribute('data-spell', r.id);
    tr.append(el('td', 'recap-spell-name', r.label), el('td', 'num', String(r.count)), el('td', 'num', formatPercent1(r.share)));
    body.appendChild(tr);
  }
  table.appendChild(body);
  return table;
}

/** Somme lisible : entier groupé par milliers (« 1 204 »), sinon une décimale (« 3,5 »). */
function sumText(n: number): string {
  return Number.isInteger(n) && Math.abs(n) < 1e9 ? formatGold(n) : formatNumber(n, 1);
}

/** Ligne `D_L = faces Maîtres 37 + faces Basiques 1 204 + plats 3 = X` — des **sommes**, plus les listes de faces (A122-10). */
function lineDL(d: ThrowDetail): string {
  const parts = [
    `faces Maîtres ${sumText(d.baseMaster)}`,
    `faces Basiques ${sumText(d.baseBasic)}`,
    `plats ${sumText(d.flat)}`,
  ];
  return `D_L = ${parts.join(' + ')} = ${formatNumber(d.DL, 1)}`;
}

/**
 * Ligne `D_C`. Pas de critique : « D_C = 1 ». Sinon le calcul, **diviseur de
 * l'ennemi compris** (A122-8 / A27 : le critique est divisé, avec un plancher
 * à ×1) : « D_C = (1 + 0,5 + 0,5) ÷ 3 = 0,67 → 1 (plancher) ».
 */
export function lineDC(d: ThrowDetail): string {
  if (!d.crit) return 'D_C = 1 (pas de critique)';
  const additive =
    d.critAdditive.length > 0
      ? `(${d.critAdditive.map((n) => formatNumber(n)).join(' + ')})`
      : `(${formatNumber(d.DC)})`;
  const mult =
    d.critMultipliers.length > 0
      ? ` × (${d.critMultipliers.map((n) => formatNumber(n)).join(' × ')})`
      : '';
  if (d.critDivisor === 1 || d.critAdditive.length === 0) {
    const divide = d.critDivisor !== 1 ? ` ÷ ${formatNumber(d.critDivisor)}` : '';
    return `D_C = ${additive}${mult}${divide} = ${formatNumber(d.DC)}`;
  }
  const raw = d.critAdditive.reduce((a, b) => a + b, 0) * d.critMultipliers.reduce((a, b) => a * b, 1) / d.critDivisor;
  const floored = raw < 1 - 1e-9 ? ` → ${formatNumber(d.DC)} (plancher)` : '';
  return `D_C = ${additive}${mult} ÷ ${formatNumber(d.critDivisor)} = ${formatNumber(raw)}${floored}`;
}

/**
 * Ligne `D_S = 1,25 × 2 × 1,1 × 1,12 = Z`, ou « pas de sort ».
 *
 * v0.5.0 : plusieurs sorts peuvent tomber sur le même lancer (DECISIONS §E A9)
 * — la ligne liste **tous** les multiplicateurs de sorts, puis les facteurs
 * d'effets (`Puissance des sorts`) fournis par le cœur dans
 * `breakdown.spellEffectMultipliers`, et le détail des sorts suit juste après.
 */
function lineDS(d: ThrowDetail): string {
  if (d.spells.length === 0) return 'D_S = 1 (pas de sort)';
  // Les facteurs neutres (×1) sont tus : un sort de secondes pures (« 42 ») ou
  // une Puissance des sorts à 0 remplirait la ligne de « × 1 » illisibles.
  const factors = [
    ...d.spells.filter((s) => s.multiplier !== 1).map((s) => formatNumber(s.multiplier)),
    ...d.spellEffectMultipliers.filter((n) => n !== 1).map((n) => formatNumber(n)),
  ];
  const names = d.spells.map((s) => s.label).join(', ');
  if (factors.length === 0) return `D_S = 1 (${names})`;
  return `D_S = ${factors.join(' × ')} = ${formatNumber(d.DS)} (${names})`;
}

/**
 * Détail d'un sort déclenché : « Paire ×1,25 », « 42 +3 s », « Nombre d'or
 * or ×1,61 ». Une ligne par sort, emplacement comme dé.
 */
export function spellLines(d: ThrowDetail): string[] {
  return d.spells.map((s) => `${s.kind === 'die' ? 'Sort de dé' : "Sort d'emplacement"} — ${triggeredSpellText(s)}`);
}

/**
 * Facteurs d'effets de sorts nommés. Le cœur publie `[spellPower]` :
 * *Sorcellerie* (v0.7.0) est **repliée dans** la Puissance des sorts, les deux
 * nœuds étant multiplicatifs entre eux (A21) — le libellé le dit plutôt que de
 * laisser croire que le talent n'a pas d'effet. *Préparation* n'y figure plus
 * (A100-2 : elle renforce chaque sort, voir `prepLines`) ; un autre facteur
 * publié plus tard sortirait sous « Effet de sort 2 » au lieu d'être tu.
 */
function spellFactorLines(d: ThrowDetail): string[] {
  if (d.spells.length === 0 || d.spellEffectMultipliers.length === 0) return [];
  const labels = ['Puissance des sorts × Sorcellerie'];
  return d.spellEffectMultipliers
    .map((n, i) => ({ n, label: labels[i] ?? `Effet de sort ${i + 1}` }))
    .filter((f) => f.n !== 1)
    .map((f) => `${f.label} ×${formatNumber(f.n)}`);
}

/**
 * *Préparation* et Mage Maudit (A100) : la ligne « Préparation ×k » quand le
 * cœur publie un facteur global > 1 (le détail par sort, « ×m → ×m′
 * (Préparation) », est dans `spellLines`), et « Sorts ÷ 2 (Mage Maudit) » quand
 * la part de bonus des sorts est divisée. Rien sans l'un ni l'autre — ni sans le cœur A100.
 */
export function prepLines(d: ThrowDetail): string[] {
  const lines: string[] = [];
  if (d.prepMult > 1) lines.push(`Préparation ×${formatNumber(d.prepMult)}`);
  if (d.spellsHalved) lines.push('Sorts ÷ 2 (Mage Maudit)');
  return lines;
}

/**
 * Facteurs nommés que le cœur publie en plus de la formule historique
 * (v0.7.0 : Larbin ×0,75, *Premiers*, *ENFER*, *Sorcellerie*, *Critique
 * Magique*…). **Tout est affiché**, y compris un nom que cette version ne
 * connaît pas : il sort alors sous son nom brut plutôt que d'être avalé en
 * silence (un facteur invisible rendrait le récapitulatif faux).
 */
function extraFactorLines(d: ThrowDetail): string[] {
  return d.extraFactors
    .filter((f) => f.value !== 1)
    .map((f) => `${f.label} ${f.multiplicative ? `×${formatNumber(f.value)}` : formatNumber(f.value)}`);
}

/**
 * Lignes des Grimoires et des Matériaux (A95), seulement quand le facteur est
 * ≠ 1 ou le report > 0. Les facteurs des Grimoires (compris dans D_S) et de
 * Ferreux (compris dans D_L) sont des **traces** ; « Brisé » entre dans le
 * produit du total ; les maximums du lancer précédent et le report de Silicium
 * disent d'où vient un total qui ne se retrouve pas par le seul produit.
 */
export function materialLines(d: ThrowDetail): string[] {
  const t = d.trace;
  const lines: string[] = [];
  if (t.grimoireNumbers !== 1) lines.push(`Grimoire des nombres ×${formatNumber(t.grimoireNumbers)}`);
  if (t.grimoireFaces !== 1) lines.push(`Face je gagne ×${formatNumber(t.grimoireFaces)}`);
  if (t.ironBasic !== 1) lines.push(`Basiques ×${formatNumber(t.ironBasic)} (Ferreux)`);
  if (t.woodCarry > 0) lines.push('Contenant : max du lancer précédent');
  if (t.broken !== 1) lines.push(`Brisé ×${formatNumber(t.broken)}`);
  if (t.voidCarry > 0) lines.push('Lancer : max du précédent');
  if (t.overkillCarry > 0) lines.push(`Report Silicium +${formatNumber(t.overkillCarry, 1)}`);
  return lines;
}

/**
 * Lignes des nouveautés de l'A98, seulement quand elles ont joué : Basiques
 * doublés par Vingt-dicte, Armure de l'ennemi (÷ 1,05^k), dégâts que la
 * marque de Null accumule (A100) ou libère avec elle, part Mult_Corrupted des dés Corrupted. Le
 * `damageDealt` du lancer sert à retrouver une libération quand le cœur ne la
 * publie pas lui-même (un lancer de Null qui inflige plus que son total).
 */
export function a98Lines(d: ThrowDetail, damageDealt?: number, game?: Game | null): string[] {
  const t = d.a98;
  const lines: string[] = [];
  if (t.basicDoubled) lines.push('Basiques doublés (Vingt-dicte)');
  if (t.armorDivisor > 1) {
    // 1,05^k : on retrouve k pour que la ligne dise le facteur et le nombre de coups.
    const k = Math.round(Math.log(t.armorDivisor) / Math.log(1.05));
    const exact = k >= 1 && Math.abs(Math.pow(1.05, k) - t.armorDivisor) < 1e-6;
    lines.push(
      exact
        ? `Armure ×1/1,05^${k} (÷ ${formatNumber(t.armorDivisor)})`
        : `Armure ×1/${formatNumber(t.armorDivisor)}`,
    );
  }
  // A100 : la marque ne retient plus rien, le lancer frappe et s'y ajoute.
  if (t.nullHeld > 0) {
    lines.push(`Marque : +${formatDamage(t.nullHeld)} accumulés`);
  } else {
    const released =
      t.nullReleased > 0
        ? t.nullReleased
        : d.spells.some((s) => s.id === 'nullMark') && typeof damageDealt === 'number' && damageDealt > d.total + 1e-6
          ? damageDealt - d.total
          : 0;
    if (released > 0) lines.push(`Marque libérée : +${formatDamage(released)}`);
  }
  if (t.corruptedBonus > 0) {
    lines.push(`Dés Corrupted ×${formatNumber(corruptedFactor(game), 0)} : +${formatNumber(t.corruptedBonus, 1)}`);
  }
  return lines;
}

/**
 * Décomposition du **Total** (A122-8) : produit des facteurs, termes **additifs**
 * (reports de Silicium et de Du Vide, marque de Null libérée) et diviseur
 * d'armure — `valeur = (produit + Σ termes) ÷ armure`, ce que le cœur calcule
 * (`buildThrow`). Les facteurs à 1 restent affichés : c'est la formule.
 */
export interface TotalParts {
  /** Facteurs du produit, formatés, dans l'ordre d'affichage. */
  factors: string[];
  /** Valeur numérique du produit (D_L × D_C × …). */
  product: number;
  terms: { label: string; value: number }[];
  armor: number;
  /** `(produit + Σ termes) ÷ armure`, à comparer au total publié. */
  value: number;
}

/** Facteur du *Croupier* (A123-3) : « Croupier ×4 », « Croupier ×0,01 ». */
export function croupierText(mult: number): string {
  return `Croupier ×${formatNumber(mult, 2)}`;
}

export function totalParts(d: ThrowDetail): TotalParts {
  const list: { text: string; value: number }[] = [
    { text: formatNumber(d.DL, 1), value: d.DL },
    { text: formatNumber(d.DC), value: d.DC },
    { text: formatNumber(d.DS), value: d.DS },
    { text: formatNumber(d.MP), value: d.MP },
    { text: formatNumber(d.MT), value: d.MT },
  ];
  const push = (v: number): void => {
    list.push({ text: formatNumber(v), value: v });
  };
  if (d.affixMult !== 1) push(d.affixMult);
  // A95 : l'ennemi brisé (Osseux) multiplie le total ; les autres traces n'entrent pas dans le produit.
  if (d.trace.broken !== 1) push(d.trace.broken);
  // A122 : Électrification (Silicium 5) et ArchiDicer entrent dans le produit.
  if (d.electrifyMult !== 1) push(d.electrifyMult);
  if (d.archidicerMult !== 1) push(d.archidicerMult);
  // A123-3 : le Croupier (×0,01 ou ×4) entre dans le produit.
  if (d.croupierMult !== 1) list.push({ text: croupierText(d.croupierMult), value: d.croupierMult });
  // Seuls les facteurs annoncés comme multiplicateurs entrent dans le produit :
  // une valeur informative publiée par le cœur (un compteur, une durée) serait
  // sinon multipliée à tort, et la ligne ne retomberait plus sur le total.
  for (const f of d.extraFactors) {
    if (f.multiplicative && f.value !== 1) push(f.value);
  }
  const product = list.reduce((m, f) => m * f.value, 1);
  const terms: TotalParts['terms'] = [];
  if (d.trace.overkillCarry > 0) terms.push({ label: 'Silicium', value: d.trace.overkillCarry });
  if (d.nullIncluded > 0) terms.push({ label: 'marque', value: d.nullIncluded });
  if (d.trace.voidCarry > 0) terms.push({ label: 'Du Vide', value: d.trace.voidCarry });
  const armor = d.a98.armorDivisor > 1 ? d.a98.armorDivisor : 1;
  const value = (product + terms.reduce((s, t) => s + t.value, 0)) / armor;
  return { factors: list.map((f) => f.text), product, terms, armor, value };
}

/**
 * Ligne `Total = X × Y × Z × … = T`, ou avec reports et armure :
 * « Total = (500 × 2 × 2 + 8 000 Silicium + 1 200 marque) ÷ 1,1025 = … ».
 */
export function lineTotal(d: ThrowDetail): string {
  const p = totalParts(d);
  let expr = p.factors.join(' × ');
  for (const t of p.terms) expr += ` + ${sumText(t.value)} ${t.label}`;
  if (p.armor > 1) expr = `(${expr}) ÷ ${formatNumber(p.armor, 4)} (armure)`;
  return `Total = ${expr} = ${formatDamage(d.total)}`;
}

/** Ligne du rapport d'un lancer qui a doublé le poison de sa cible (A123-2). */
export const ANDARIEL_LINE = 'Andarielle : poison doublé';

/**
 * Résumé **court** d'un lancer (hors debug, A122-10) : somme des faces Maîtres,
 * contenant, sorts, reports, Électrification et ArchiDicer s'ils jouent,
 * dégâts infligés. Ni D_L / D_C / D_S / M_P / M_T, ni liste dé par dé.
 */
export function shortDetailLines(t: CombatThrow): string[] {
  const d = throwDetail(t.result);
  const lines: string[] = [];
  if (t.dodged) lines.push("Esquivé : ce lancer n'a eu aucun effet.");
  lines.push(`Faces Maîtres : ${sumText(d.baseMaster)}`);
  const container = diceLines(t.result).find((l) => l.kind === 'container');
  if (container) lines.push(container.text);
  lines.push(d.spells.length > 0 ? `Sorts : ${d.spells.map((s) => s.label).join(', ')}` : 'Sorts : aucun');
  if (d.trace.overkillCarry > 0) lines.push(`Report Silicium : +${sumText(d.trace.overkillCarry)}`);
  if (d.nullIncluded > 0) lines.push(`Marque libérée : +${sumText(d.nullIncluded)}`);
  if (d.trace.voidCarry > 0) lines.push(`Report Du Vide : +${sumText(d.trace.voidCarry)}`);
  if (d.electrifyMult !== 1) {
    lines.push(`Électrification ×${formatNumber(d.electrifyMult)}${d.electrifyStacks > 0 ? ` (${d.electrifyStacks} cumul${d.electrifyStacks > 1 ? 's' : ''})` : ''}`);
  }
  if (d.archidicerMult !== 1) lines.push(`ArchiDicer ×${formatNumber(d.archidicerMult)}`);
  // A123 : Croupier (facteur du lancer) et Andarielle (poison doublé à l'application).
  if (d.croupierMult !== 1) lines.push(croupierText(d.croupierMult));
  if (d.poisonDoubled) lines.push(ANDARIEL_LINE);
  lines.push(`Infligés : ${formatDamage(t.damageDealt)}${t.killed ? ' · ennemi tué' : ''}`);
  return lines;
}

/** Une ligne de la liste des dés d'un lancer (A78). */
export interface DiceLine {
  /** `master` : un Maître ; `container` : tous les Basiques ; `absorbed` : un dé absorbé. */
  kind: 'master' | 'container' | 'absorbed';
  text: string;
}

/**
 * Niveau d'un dé Maître et facteur de dégâts correspondant (A94), retrouvés par
 * l'identifiant du dé ; `null` pour un dé inconnu (consommé depuis) ou sans niveau.
 */
export type DieLevelLookup = (dieId: string) => { level: number; factor: number } | null;

/** Recherche de niveau branchée sur l'inventaire de la partie. */
export function dieLevelLookupOf(game: Game): DieLevelLookup {
  return (dieId) => {
    const die = game.state.inventory.find((d) => d.id === dieId);
    if (!die) return null;
    const level = dieLevelOf(die);
    return level > 0 ? { level, factor: levelFactorOf(game, die) } : null;
  };
}

/**
 * Suffixe d'un dé que le cœur marque : « (rebond) » (Caoutchouc), « (fantôme) »
 * (Silicium) — A95 — puis, depuis l'A98, « (copie 42) » (le dé relancé par le
 * sort 42), « (réplique) » (la copie de Réplicant) et « (annulé) » (dé retiré
 * du lancer par un boss). Plusieurs marques se lisent ensemble : « (rebond, réplique) ».
 */
function dieMarker(die: ThrowResult['dice'][number]): string {
  const marks = die as { bounce?: boolean; ghost?: boolean; copyOf?: string; voided?: boolean };
  const out: string[] = [];
  if (marks.ghost === true) out.push('fantôme');
  if (marks.bounce === true) out.push('rebond');
  if (marks.copyOf === '42') out.push('copie 42');
  if (marks.copyOf === 'replicant') out.push('réplique');
  if (marks.voided === true) out.push('annulé');
  return out.length > 0 ? ` (${out.join(', ')})` : '';
}

/**
 * Lignes de la liste des dés d'un lancer (A78) : les **Maîtres** restent
 * détaillés un par un, les dés **Basiques** du contenant tiennent en **une
 * ligne** « Contenant : N dés · somme X ». N compte les Basiques lancés, ceux
 * du tractopelle compris (« dont tractopelle M » quand il y en a) ; X est la
 * somme de leurs dégâts après la division éventuelle (*Mutilant*, Mygale…,
 * `basicDivisor`). Un dé absorbé garde sa ligne barrée et n'entre pas dans la
 * somme ; sans Basique, pas de ligne de contenant.
 */
export function diceLines(result: ThrowResult, levelOf?: DieLevelLookup): DiceLine[] {
  const d = throwDetail(result);
  const lines: DiceLine[] = [];
  let basics = 0;
  let basicSum = 0;
  for (const die of result.dice) {
    if (!die.isMaster && !die.absorbed) {
      basics++;
      basicSum += die.damage;
      continue;
    }
    // Un dé Maître de niveau (A94) : « face 4 · Nv 7 ×17 → 68,3 ».
    const lv = die.isMaster && die.dieId && levelOf ? levelOf(die.dieId) : null;
    const label = [
      `d${die.sides}`,
      lv
        ? `face ${die.face} · Nv ${lv.level} ${formatLevelFactor(lv.factor)} → ${formatNumber(die.face * lv.factor, 1)}`
        : `face ${die.face}`,
      // A95 : le second lancer d'un dé en rebond (Caoutchouc) et le dé fantôme (Silicium).
      `${die.isMaster ? 'Maître' : 'Basique'}${dieMarker(die)}`,
      `${formatDamage(die.damage)} dégâts`,
    ];
    // Un dé annulé par un boss le dit déjà dans son suffixe ; seul le bouclier « absorbe ».
    if (die.absorbed && (die as { voided?: boolean }).voided !== true) label.push('absorbé');
    lines.push({ kind: die.absorbed ? 'absorbed' : 'master', text: label.join(' · ') });
  }
  // Les dés du tractopelle ne sont pas dans `dice` : seule leur somme brute est publiée.
  const backhoe = Math.max(0, Math.floor(d.backhoeDice));
  const count = basics + backhoe;
  if (count > 0) {
    const sum = (basicSum + d.backhoeBasic) / (d.basicDivisor > 0 ? d.basicDivisor : 1);
    const dice = `${formatGold(count)} ${count === 1 ? 'dé' : 'dés'}`;
    const from = backhoe > 0 ? ` (dont tractopelle ${formatGold(backhoe)})` : '';
    lines.push({ kind: 'container', text: `Contenant : ${dice}${from} · somme ${formatNumber(sum, 1)}` });
  }
  return lines;
}

/** Détail de calcul d'un lancer (COMMENTS §1.2.5). */
function buildThrowDetail(t: CombatThrow, levelOf?: DieLevelLookup, game?: Game | null, debug = true): HTMLElement {
  const d = throwDetail(t.result);
  const wrap = el('div', 'recap-detail');
  wrap.setAttribute('data-role', 'recap-detail');
  wrap.setAttribute('data-detail', debug ? 'full' : 'short');

  // A122-10 : hors debug, un résumé court — le calcul détaillé est réservé au mode debug.
  if (!debug) {
    const short = el('ul', 'recap-short');
    short.setAttribute('data-role', 'recap-short');
    for (const line of shortDetailLines(t)) short.appendChild(el('li', undefined, line));
    wrap.appendChild(short);
    return wrap;
  }

  const lines = el('ul', 'recap-formula');
  const push = (text: string, cls?: string): void => {
    lines.appendChild(el('li', cls, text));
  };
  if (t.dodged) {
    wrap.appendChild(el('p', 'recap-dodged', "Esquivé : ce lancer n'a eu aucun effet (A29)."));
  }
  push(lineDL(d));
  // A94 : les niveaux des dés relèvent D_L ; la ligne dit de combien.
  if (d.dieLevelBonus > 0) {
    push(`Niveaux des dés : +${formatNumber(d.dieLevelBonus, 1)}`, 'recap-formula-factor');
  }
  // v0.26.0 (A72-17) : les dés du tractopelle ne sont pas listés dé par dé.
  if (d.backhoeDice > 0) {
    push(
      `Tractopelle : +${formatGold(d.backhoeDice)} dés Basiques, faces ${formatNumber(d.backhoeBasic, 0)} (comprises dans D_L)`,
      'recap-formula-factor',
    );
  }
  push(lineDC(d));
  push(lineDS(d));
  for (const line of spellLines(d)) push(line, 'recap-formula-spell');
  for (const line of spellFactorLines(d)) push(line, 'recap-formula-spell');
  // Préparation (A100-2) et Mage Maudit (A100-1).
  for (const line of prepLines(d)) push(line, 'recap-formula-factor');
  for (const line of extraFactorLines(d)) push(line, 'recap-formula-factor');
  // Grimoires et Matériaux (A95).
  for (const line of materialLines(d)) push(line, 'recap-formula-factor');
  // Vingt-dicte, Armure, marque de Null et dés Corrupted (A98).
  for (const line of a98Lines(d, t.damageDealt, game)) push(line, 'recap-formula-factor');
  // A122 : Électrification (Silicium 5) et ArchiDicer, facteurs du produit.
  if (d.electrifyMult !== 1) push(`Électrification ×${formatNumber(d.electrifyMult)}`, 'recap-formula-factor');
  if (d.archidicerMult !== 1) push(`ArchiDicer ×${formatNumber(d.archidicerMult)}`, 'recap-formula-factor');
  // Effets d'ennemi sur la formule (v0.8.0, §F) : diviseur des dés Basiques,
  // emplacements bloqués (« Sorts ÷ 2 » du Mage Maudit : `prepLines`). Le diviseur
  // critique est dans la ligne D_C (A122). Neutres (÷1, faux, 0) tus.
  if (d.basicDivisor !== 1) push(`Basiques ÷ ${formatNumber(d.basicDivisor)} (ennemi)`, 'recap-formula-factor');
  if (d.blockedSlots > 0) {
    push(
      `${d.blockedSlots} emplacement${d.blockedSlots > 1 ? 's' : ''} bloqué${d.blockedSlots > 1 ? 's' : ''} (ennemi)`,
      'recap-formula-factor',
    );
  }
  push(`M_P = ${formatNumber(d.MP)}`);
  push(`M_T = ${formatNumber(d.MT)}`);
  if (d.affixMult !== 1) push(`Exécutant ×${formatNumber(d.affixMult)}`);
  push(lineTotal(d), 'recap-formula-total');
  // v0.26.0 : Triche (A72-6) et double coup de Guinsoo (A72-15).
  if (d.cheat) {
    push(
      `Triche : lancer ${d.cheat.kept} gardé sur 2 (l’autre faisait ${formatDamage(d.cheat.discardedTotal)})`,
      'recap-formula-factor',
    );
  }
  if (d.hits === 2) push('Guinsoo : dégâts appliqués deux fois (2 coups)', 'recap-formula-factor');
  // A123-2 : le poison de la cible a été doublé à l'application de ce lancer.
  if (d.poisonDoubled) push(ANDARIEL_LINE, 'recap-formula-factor');
  wrap.appendChild(lines);

  // Faces dé par dé — le calcul ci-dessus les agrège, la liste les déplie.
  // Depuis la v0.33.0 (A78), les Basiques du contenant tiennent en une ligne.
  const dice = el('ul', 'recap-dice');
  for (const line of diceLines(t.result, levelOf)) {
    const cls = ['recap-die'];
    if (line.kind === 'absorbed') cls.push('recap-die--absorbed');
    if (line.kind === 'container') cls.push('recap-die--container');
    const li = el('li', cls.join(' '), line.text);
    li.setAttribute('data-die', line.kind);
    dice.appendChild(li);
  }
  wrap.appendChild(dice);

  const applied = el(
    'p',
    'recap-applied',
    `Infligés : ${formatDamage(t.damageDealt)}${t.killed ? ' · ennemi tué' : ''}`,
  );
  wrap.appendChild(applied);

  if (!d.exact) {
    wrap.appendChild(
      el(
        'p',
        'recap-approx',
        'Détail reconstitué : le cœur ne fournit pas encore ThrowResult.breakdown.',
      ),
    );
  }
  return wrap;
}

/** Étiquette d'un lancer : badge F1 teinté (`recap-tag--<kind>` reste le crochet des tests). */
function tag(kind: string, tone: string, text: string): HTMLElement {
  const node = el('span', `f-badge recap-tag recap-tag--${kind}`, text);
  node.setAttribute('data-tone', tone);
  return node;
}

/** Une ligne de lancer, dépliable au tap (`<details>` natif). */
function buildThrowRow(t: CombatThrow, index: number, levelOf?: DieLevelLookup, game?: Game | null, debug = true): HTMLElement {
  const row = el('details', 'recap-throw');
  row.setAttribute('data-role', 'recap-throw');
  row.setAttribute('data-throw', String(t.id));

  const summary = el('summary', 'recap-throw-head');
  const parts = [
    `#${index}`,
    atLabel(t.at),
    t.enemyName,
    `${formatDamage(t.result.total)} dégâts`,
  ];
  summary.appendChild(el('span', 'recap-throw-line num', parts.join(' · ')));

  // Étiquettes = badges F1 (`.f-badge`, liseré de la teinte `data-tone`).
  const tags = el('span', 'recap-tags');
  if (t.dodged) tags.appendChild(tag('dodge', 'dim', 'Esquivé'));
  if (t.result.crit === true) tags.appendChild(tag('crit', 'gold', 'CRIT'));
  if ((t.result as { hits?: number }).hits === 2) tags.appendChild(tag('double', 'gold', '×2 coups'));
  if ((t.result as { backhoe?: number }).backhoe) tags.appendChild(tag('backhoe', 'dim', 'Tractopelle'));
  // A123 : Croupier (×4 doré, ×0,01 grisé) et Andarielle (poison doublé).
  const croupier = throwDetail(t.result).croupierMult;
  if (croupier !== 1) {
    tags.appendChild(tag('croupier', croupier > 1 ? 'gold' : 'dim', croupierText(croupier)));
  }
  if ((t.result as { poisonDoubled?: boolean }).poisonDoubled === true) {
    tags.appendChild(tag('andariel', 'good', 'Poison doublé'));
  }
  for (const spell of triggeredSpells(t.result)) {
    tags.appendChild(tag('spell', 'spell', spell.label));
  }
  if (t.killed) tags.appendChild(tag('kill', 'good', 'Tué'));
  if (tags.childElementCount > 0) summary.appendChild(tags);
  row.appendChild(summary);

  // Le détail n'est construit qu'à la première ouverture : une Invasion peut
  // compter des centaines de lancers, et l'écran de fin doit rester instantané.
  let built = false;
  row.addEventListener('toggle', () => {
    if (!row.open || built) return;
    built = true;
    row.appendChild(buildThrowDetail(t, levelOf, game, debug));
  });

  return row;
}

/**
 * Ordre d'affichage de la liste (A122-15) : du plus récent au plus ancien, les
 * morts par poison intercalées à leur instant ; à égalité d'instant, la mort
 * (conséquence) passe avant le lancer. `index` : position dans `throws` ou `kills`.
 */
export function recapOrder(
  throws: readonly { at: number }[],
  kills: readonly { at: number }[],
): { kind: 'throw' | 'poison'; index: number }[] {
  const entries = [
    ...throws.map((t, index) => ({ kind: 'throw' as const, index, at: t.at })),
    ...kills.map((k, index) => ({ kind: 'poison' as const, index, at: k.at })),
  ];
  entries.sort((a, b) => b.at - a.at || (a.kind === b.kind ? b.index - a.index : a.kind === 'poison' ? -1 : 1));
  return entries.map(({ kind, index }) => ({ kind, index }));
}

/** Ligne d'un ennemi tué par le poison, intercalée à son instant (A122-15). */
function buildPoisonKillRow(k: PoisonKill): HTMLElement {
  const li = el('li', 'recap-row recap-row--poison');
  li.setAttribute('data-role', 'recap-poison-kill');
  li.appendChild(el('p', 'recap-poison-kill num', `${atLabel(k.at)} · ${k.enemyName} · tué par le poison`));
  return li;
}

export interface DamageRecapOptions {
  /** Section dépliée d'entrée (vrai en fin d'Expédition, faux ailleurs). */
  open: boolean;
  /** Détail de calcul au tap : `true` (debug) → calcul complet ; `false` → résumé court. Défaut : `isDebugEnabled()`. */
  debug?: boolean;
  /** Journal à montrer (A122-11 : le dernier combat) ; défaut : `state.combat`. */
  log?: CombatLog | null;
}

/**
 * Construit la section « Récapitulatif des dégâts ».
 * `open` : dépliée d'entrée (vrai en fin d'Expédition, faux ailleurs).
 */
export function buildDamageRecap(game: Game, opts: DamageRecapOptions): HTMLElement {
  // Panneau facetté repliable (`.f-details`, refonte F1 lot 3, v0.16.0).
  const section = el('details', 'f-panel f-details recap');
  section.setAttribute('data-role', 'damage-recap');
  section.open = opts.open;
  section.appendChild(el('summary', 'recap-summary', 'Récapitulatif des dégâts'));
  const debug = opts.debug ?? isDebugEnabled();

  const log = opts.log !== undefined ? opts.log : combatLog(game);
  if (!log) {
    section.appendChild(el('p', 'recap-empty', 'Historique indisponible'));
    return section;
  }
  if (log.throws.length === 0) {
    section.appendChild(el('p', 'recap-empty', 'Aucun lancer enregistré.'));
    return section;
  }

  const m = computeMetrics(log.throws, log.durationPlayed);
  section.appendChild(buildMetrics(m));
  if (log.poisonDamage > 0) {
    const poison = el('p', 'recap-poison num', `Poison : ${formatDamage(log.poisonDamage)} dégâts`);
    poison.setAttribute('data-role', 'recap-poison');
    section.appendChild(poison);
  }
  const table = buildSpellTable(m.spellRows);
  if (table) section.appendChild(table);
  section.appendChild(
    el('p', 'recap-duration', `Durée jouée : ${formatSeconds(m.duration)} · ${log.kind === 'invasion' ? 'Invasion' : 'Expédition'}`),
  );

  const list = el('ol', 'recap-list');
  list.setAttribute('data-role', 'recap-list');
  const levelOf = dieLevelLookupOf(game);
  // Du plus récent au plus ancien : le dernier lancer est celui que le joueur
  // vient de voir, c'est lui qu'il cherche en premier. Les morts par poison
  // s'intercalent à leur instant (à égalité, la mort est plus récente que le lancer).
  for (const e of recapOrder(log.throws, log.poisonKills)) {
    if (e.kind === 'poison') {
      list.appendChild(buildPoisonKillRow(log.poisonKills[e.index]));
      continue;
    }
    const li = el('li', 'recap-row');
    li.appendChild(buildThrowRow(log.throws[e.index], e.index + 1, levelOf, game, debug));
    list.appendChild(li);
  }
  section.appendChild(list);
  return section;
}

/**
 * Fenêtre fermable du **dernier rapport de combat** (A122-11), ouverte depuis
 * l'Accalmie. Rien à ouvrir (`false`) tant qu'aucun combat n'a eu lieu dans la session.
 */
export function openLastRecapModal(game: Game): boolean {
  const log = lastCombatLog(game);
  if (!log) return false;
  const overlay = el('div', 'f-scrim');
  overlay.setAttribute('data-role', 'last-recap-modal');
  overlay.innerHTML = `
    <div class="f-modal last-recap-modal" role="dialog" aria-modal="true" aria-labelledby="last-recap-title">
      <button type="button" class="f-ibtn equip-modal-close" data-action="close-last-recap" aria-label="Fermer">${icon('close')}</button>
      <h2 class="f-modal-title" id="last-recap-title">Dernier rapport de combat</h2>
      <div class="last-recap-body" data-role="last-recap-body"></div>
    </div>
  `;
  overlay.querySelector('[data-role="last-recap-body"]')?.appendChild(buildDamageRecap(game, { open: true, log }));
  let unregisterEscape = (): void => {};
  const close = (): void => {
    unregisterEscape();
    overlay.remove();
  };
  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) close();
  });
  overlay.querySelector('[data-action="close-last-recap"]')?.addEventListener('click', close);
  overlayRoot().appendChild(overlay);
  unregisterEscape = registerEscape(overlay, close);
  overlay.querySelector<HTMLButtonElement>('[data-action="close-last-recap"]')?.focus();
  return true;
}
